<template>
    <section>
        <div class="custom-form">
            <div class="search-save-button">
                <div class="search">
                    <ds-input-field
                        v-model="searchCriteria"
                        class="search-input-field"
                        label="Search contact info"
                    >
                        <template #trailing>
                            <span
                                v-show="searchCriteria"
                                class="clear-icon"
                                @click="clearSearch"
                            >
                                <ds-icon name="x" />
                            </span>
                        </template>
                    </ds-input-field>
                </div>

                <contact-custom-header
                    :custom-tab-map="customTabMap"
                    :contact-id="contact.id"
                    :custom-watcher-map="customWatcherMap"
                    :tab-id="customTabId"
                />
            </div>

            <div v-if="checkCustomFieldsEmpty" class="no-custom-fields">
                <div class="no-custom-label">
                    <p class="no-custom-text">
                        You have no custom fields
                    </p>

                    <ds-text-button data-qa="manage-custom-fields" @click="manageCustomField">
                        Manage custom fields
                    </ds-text-button>
                </div>
            </div>

            <div
                v-for="(customGroup, i) in customTab.customGroups"
                v-else
                :key="i"
            >
                <h3 v-if="checkHeadersDisplay(customGroup)" class="custom-fields-group custom-fields-group-header" data-qa="group-header">
                    {{ customGroup.groupName }}
                </h3>

                <div class="custom-fields-grid">
                    <div v-for="(customField, index) in customFields" :key="index" class="custom-fields-container">
                        <transition v-if="customField.groupId === customGroup.id">
                            <ds-input-field
                                v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.CURRENCY, customGroup, customGroup)"
                                :ref="`currency${index}`"
                                v-model.trim="customField.fieldValue"
                                data-qa="customField_CURRENCY"
                                class="input-field"
                                type="number"
                                :step="0.01"
                                :label="customField.label"
                                :name="`custom-field-${customField.id}`"
                                :intercept-on-update="interceptInput(customField, `currency${index}`) && displayField(customField.fieldName, customGroup)"
                                :submitted="submitted"
                            />

                            <ds-date-picker
                                v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.DATE, customGroup)"
                                v-model.trim="customField.fieldValue"
                                data-qa="customField_DATE"
                                class="input-field"
                                :label="customField.label"
                                :name="`custom-field-${customField.id}`"
                                :submitted="submitted"
                                date-only
                            />

                            <ds-date-picker
                                v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.DATETIME, customGroup)"
                                :ref="`dateTime${index}`"
                                v-model="customField.fieldValue"
                                data-qa="customField_DATETIME"
                                class="input-field"
                                time
                                :name="`custom-field-${customField.id}`"
                                :label="customField.label"
                                :submitted="submitted"
                                :intercept-on-update="interceptInput(customField, `dateTime${index}`)"
                            />

                            <ds-select-field
                                v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.DAY_OF_WEEK, customGroup)"
                                v-model="customField.fieldValue"
                                data-qa="customField_DAY_OF_WEEK"
                                class="input-field"
                                bind-value-only
                                value-prop="value"
                                :label="customField.label"
                                :name="`custom-field-${customField.id}`"
                                :options="formatOptions(customField)"
                                :submitted="submitted"
                            />

                            <ds-input-field
                                v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.DECIMAL_NUMBER, customGroup)"
                                :ref="`decimalNumber${index}`"
                                v-model.trim="customField.fieldValue"
                                data-qa="customField_DECIMAL_NUMBER"
                                class="input-field"
                                :label="customField.label"
                                :name="`custom-field-${customField.id}`"
                                :submitted="submitted"
                                :intercept-on-update="interceptInput(customField, `decimalNumber${index}`)"
                            />

                            <ds-multiselect
                                v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.DROPDOWN, customGroup)"
                                v-model="customField.fieldValue"
                                data-qa="customField_DROPDOWN"
                                class="input-field"
                                bind-value-only
                                show-blank
                                :label="customField.label"
                                :placeholder="customField.label"
                                :name="`custom-field-${customField.id}`"
                                :options="formatOptions(customField)"
                                :submitted="submitted"
                            />

                            <ds-input-field
                                v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.SSN, customGroup)"
                                v-model="customField.fieldValue"
                                data-qa="customField_SSN"
                                class="input-field"
                                type="text"
                                :label="customField.label"
                                :name="`custom-field-${customField.id}`"
                                :submitted="submitted"
                            />

                            <ds-multiselect
                                v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.DRILLDOWN, customGroup)"
                                v-model="customField.fieldValue"
                                data-qa="customField_DRILLDOWN"
                                class="input-field"
                                bind-value-only
                                :placeholder="customField.label"
                                :label="customField.label"
                                :name="`custom-field-${customField.id}`"
                                :options="formatDrillDownOptions(customField)"
                                :submitted="submitted"
                            />

                            <ds-input-field
                                v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.EMAIL, customGroup)"
                                v-model.trim="customField.fieldValue"
                                data-qa="customField_EMAIL"
                                class="input-field"
                                type="email"
                                :label="customField.label"
                                :name="`custom-field-${customField.id}`"
                                :submitted="submitted"
                            >
                                <ds-icon-button
                                    slot="trailing"
                                    class="trailing-icon"
                                    @click="sendEmail(customField.fieldValue)"
                                >
                                    <ds-icon
                                        v-if="fieldEmptyCheck(customField.fieldValue)"
                                        class="external-look"
                                        name="send"
                                    />
                                </ds-icon-button>
                            </ds-input-field>

                            <ds-select-field
                                v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.MONTH, customGroup)"
                                v-model="customField.fieldValue"
                                data-qa="customField_MONTH"
                                class="input-field"
                                label-prop="label"
                                value-prop="value"
                                bind-value-only
                                searchable
                                :label="customField.label"
                                :name="`custom-field-${customField.id}`"
                                :options="formatOptions(customField)"
                                :submitted="submitted"
                            />

                            <ds-input-field
                                v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.NAME, customGroup)"
                                v-model="customField.fieldValue"
                                data-qa="customField_NAME"
                                class="input-field"
                                type="text"
                                :label="customField.label"
                                :name="`custom-field-${customField.id}`"
                                :submitted="submitted"
                            />

                            <ds-input-field
                                v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.PERCENT, customGroup)"
                                :ref="`percent${index}`"
                                v-model.trim="customField.fieldValue"
                                data-qa="customField_PERCENT"
                                class="input-field"
                                type="number"
                                :label="customField.label"
                                :name="`custom-field-${customField.id}`"
                                :submitted="submitted"
                                :intercept-on-update="interceptInput(customField, `percent${index}`)"
                            />

                            <ds-input-field
                                v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.PHONE_NUMBER, customGroup)"
                                :ref="`phone${index}`"
                                v-model.trim="customField.fieldValue"
                                data-qa="customField_PHONE_NUMBER"
                                class="input-field"
                                type="tel"
                                :label="customField.label"
                                :name="`custom-field-${customField.id}`"
                                :submitted="submitted"
                                :intercept-on-update="interceptInput(customField, `phone${index}`)"
                            />

                            <div v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.RADIO, customGroup)" class="radio-group-container">
                                <small>{{ customField.label }}</small>
                                <ds-radio-group
                                    v-model="customField.fieldValue"
                                    data-qa="customField_RADIO"
                                    class="input-field"
                                    :options="formatOptions(customField)"
                                    :submitted="submitted"
                                />
                            </div>

                            <ds-select-field
                                v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.STATE, customGroup)"
                                v-model="customField.fieldValue"
                                data-qa="customField_STATE"
                                class="input-field"
                                label-prop="label"
                                value-prop="value"
                                bind-value-only
                                :label="customField.label"
                                :name="`custom-field-${customField.id}`"
                                :options="formatOptions(customField)"
                                :submitted="submitted"
                            />

                            <ds-input-field
                                v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.TEXT, customGroup)"
                                v-model="customField.fieldValue"
                                data-qa="customField_TEXT"
                                class="input-field"
                                type="text"
                                :label="customField.label"
                                :name="`custom-field-${customField.id}`"
                                :submitted="submitted"
                            />

                            <ds-text-area-field
                                v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.TEXT_AREA, customGroup)"
                                v-model.trim="customField.fieldValue"
                                data-qa="customField_TEXT_AREA"
                                type="text"
                                class="text-area-format input-field"
                                :maxlength="65000"
                                :label="customField.label"
                                :name="`custom-field-${customField.id}`"
                                :submitted="submitted"
                            />

                            <ds-input-field
                                v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.WEBSITE, customGroup)"
                                :ref="`website${index}`"
                                v-model.trim="customField.fieldValue"
                                data-qa="customField_WEBSITE"
                                class="input-field"
                                type="url"
                                :label="customField.label"
                                :name="`custom-field-${customField.id}`"
                                :submitted="submitted"
                                :intercept-on-update="interceptInput(customField, `website${index}`)"
                            >
                                <ds-icon-button
                                    slot="trailing"
                                    class="trailing-icon"
                                    @click="webSite(customField.fieldValue)"
                                >
                                    <ds-icon
                                        v-if="fieldEmptyCheck(customField.fieldValue)"
                                        class="external-look"
                                        name="external-link"
                                    />
                                </ds-icon-button>
                            </ds-input-field>

                            <ds-input-field
                                v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.WHOLE_NUMBER, customGroup)"
                                v-model.trim="customField.fieldValue"
                                data-qa="customField_WHOLE_NUMBER"
                                class="input-field"
                                type="number"
                                :label="customField.label"
                                :name="`custom-field-${customField.id}`"
                                :submitted="submitted"
                            />

                            <ds-input-field
                                v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.YEAR, customGroup)"
                                v-model.trim="customField.fieldValue"
                                data-qa="customField_YEAR"
                                class="input-field"
                                type="number"
                                :label="customField.label"
                                :name="`custom-field-${customField.id}`"
                                :submitted="submitted"
                            />

                            <ds-select-field
                                v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.YES_NO, customGroup)"
                                v-model="customField.fieldValue"
                                data-qa="customField_YES_NO"
                                class="input-field"
                                label-prop="label"
                                value-prop="value"
                                bind-value-only
                                allow-null
                                :label="customField.label"
                                :name="`custom-field-${customField.id}`"
                                :options="formatYesNoOptions(customField)"
                                :submitted="submitted"
                            />

                            <ds-multiselect
                                v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.MULTISELECT, customGroup)"
                                v-model="customField.fieldValue"
                                data-qa="customField_MULTISELECT"
                                class="input-field"
                                bind-value-only
                                multiple
                                searchable
                                :label="customField.label"
                                :placeholder="customField.label"
                                :options="formatOptions(customField)"
                            />

                            <ds-select-field
                                v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.USER, customGroup)"
                                v-model="customField.fieldValue"
                                data-qa="customField_USER"
                                bind-value-only
                                class="input-field"
                                :label="customField.label"
                                :name="`custom-field-${customField.id}`"
                                :options="formatOptions(customField)"
                                :submitted="submitted"
                            />

                            <ds-multiselect
                                v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.USER_MULTISELECT, customGroup)"
                                v-model="customField.fieldValue"
                                data-qa="customField_USER_MULTISELECT"
                                class="input-field"
                                bind-value-only
                                multiple
                                searchable
                                :label="customField.label"
                                :placeholder="customField.label"
                                :options="formatOptions(customField)"
                            />
                        </transition>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import axios from 'axios';
import moment from 'moment-timezone';
import contactFieldTypes from './contactFieldTypes';
import ContactCustomHeader from './ContactCustomHeader';
import cloneDeep from 'lodash.clonedeep';

export default {
    components: {
        ContactCustomHeader,
    },

    props: {
        contact: Object,
        tabId: [String, Number],
    },

    data() {
        return {
            headerFieldsData: {},
            headerDisplay: {},
            searchCriteria: '',
            serverTimeZone: '',
            contactFieldTypes,
            customTab: Object,
            customFields: [],
            submitted: false,
            customTabId: this.tabId,
            customTabMap: new Map(),
            customTabMapCopy: new Map(),
            customFieldsCopy: {},
            isCustomFieldChanged: false,
            customWatcherMap: new Map(),
            yesNo: [
                { label: 'No', value: 0 },
                { label: 'Yes', value: 1 },
            ],
            yesNoSelected: [
                { label: 'Not selected', value: null },
                { label: 'No', value: 0 },
                { label: 'Yes', value: 1 },
            ],
        };
    },

    created() {
        this.loadCustomFields();
    },

    mounted() {
        this.$root.$on('CUSTOM_DATA', (id) => {
            this.customTabId = id;

            if (this.customTabMap.get(this.customTabId) == null) {
                this.loadCustomFields();
            } else {
                this.customFields = this.customTabMap.get(this.customTabId).formFields;
                this.customTab = this.customTabMap.get(this.customTabId).customTabs;
                this.customFieldsCopy = this.customTabMapCopy.get(this.customTabId);
            }
        });

        this.$root.$on('RESET_CUSTOM_FIELD_COPY', (reset) => {
            if (reset) {
                this.customFieldsCopy = cloneDeep(this.customFields);
            }
        });
    },

    destroyed() {
        this.$root.$off('CUSTOM_DATA');
        this.$root.$off('RESET_CUSTOM_FIELD_COPY');
    },

    watch: {
        customFields: {
            deep: true,
            handler() {
                this.isCustomFieldChanged = JSON.stringify(this.customFields) !== JSON.stringify(this.customFieldsCopy);
                this.customWatcherMap.set(this.customTabId, this.isCustomFieldChanged);

                let flag = false;

                for (const key in [...this.customWatcherMap.entries()]) {
                    if ([...this.customWatcherMap.entries()][key]) {
                        const isTabDataChanged = [...this.customWatcherMap.entries()][key];

                        if (isTabDataChanged[1] === true) {
                            flag = true;
                            break;
                        }
                    }
                }

                this.$root.$emit('IS_CONTACT_CUSTOMFIELD_CHANGED', flag);
            },
        },
    },

    computed: {
        concatHeader() {
            // appending header with id because to make header unique
            return (head, id) => {
                return `${head.replace(/\s+/g, '').toLowerCase()}_${id}`;
            };
        },

        checkHeadersDisplay() {
            return (header) => {
                const headName = this.concatHeader(header?.groupName, header?.id);

                return this.checkField(header.groupName) || this.fieldSearchHeaderDisplay(headName) || this.headerDisplay[headName];
            };
        },

        displayField() {
            // searching with field names should return true if exist
            return (field, head) => {
                field = field?.replace(/\s+/g, '').toLowerCase();
                const header = this.concatHeader(head?.groupName, head?.id);

                return this.checkField(field) || this.headerDisplay[header];
            };
        },

        fieldSearchHeaderDisplay() {
            // searching with field name should return header names ex: General information
            return (param) => {
                param = param.replace(/\s+/g, '').toLowerCase();
                const searchField = this.searchCriteria?.replace(/\s+/g, '');
                const headsToDisplay = Object.keys(this.headerFieldsData).filter((key) => {
                    return this.headerFieldsData[key].toString()?.replace(/\s+/g, '').toLowerCase().includes(searchField.toLowerCase());
                });

                return headsToDisplay.includes(param);
            };
        },

        checkField() {
            return (data) => {
                const searchField = this.searchCriteria?.replace(/\s+/g, '');

                Object.keys(this.headerDisplay).forEach((key) => {
                    this.headerDisplay[key] = key.split('_')[0].toLowerCase().includes(searchField.toLowerCase());

                    return this.headerDisplay;
                });

                return data?.replace(/\s+/g, '').toLowerCase().includes(searchField.toLowerCase());
            };
        },

        checkCustomFieldsEmpty() {
            return this.customFields === null || this.customFields.length <= 0;
        },

        checkTypeAndDisplayField() {
            return (field, type, customGroup) => {
                return field?.fieldType === type && this.displayField(field?.fieldName, customGroup);
            };
        },

    },

    methods: {
        clearSearch() {
            this.searchCriteria = '';
        },

        async loadCustomFields() {
            const response = await axios.get(`/app/customFields/getCustomTabFields?contactId=${this.contact.id}&tabId=${this.customTabId}`);

            this.serverTimeZone = response.data.serverTimeZone;

            response.data.formFields.forEach((fields) => {
                if (fields.fieldType === 'DateTime' && fields.fieldValue !== null) {
                    fields.fieldValue = this.formatDateTimeFieldToTZ(fields.fieldValue, 'YYYY-MM-DD HH:mm:ss');
                }

                return fields;
            });

            response.data.formFields.forEach((fields) => {
                if (fields.fieldType === 'UserListBox' && fields.fieldValue !== null) {
                    fields.fieldValue = fields.fieldValue.split(',');
                }

                return fields;
            });
            this.customTab = response.data.customTabs;
            this.customFields = response.data.formFields;
            this.customTabMap.set(this.customTabId, response.data);
            this.customFieldsCopy = cloneDeep(this.customFields);

            if (this.customTabMapCopy.get(this.customTabId) === undefined || this.customTabMapCopy.get(this.customTabId) === null) {
                this.customTabMapCopy.set(this.customTabId, this.customFieldsCopy);
            }

            this.customTab?.customGroups?.map((headFlds) => {
                // preparing map object, header with fields
                this.headerFieldsData[this.concatHeader(headFlds.groupName?.replace(/\s+/g, '').toLowerCase(), headFlds.id)] = this.customFields?.filter((fldGrp) => (fldGrp.groupId === headFlds.id)).map((fld) => fld.fieldName);

                // initializing header map names with true
                this.headerDisplay[this.concatHeader(headFlds.groupName.replace(/\s+/g, '').toLowerCase(), headFlds.id)] = true;

                return headFlds;
            });
        },

        async sendEmail(emailAddress) {
            try {
                const response = await axios.get('/app/contact/emailId');

                const email = response.data;

                window.open(`/Mail/compose.jsp?id=${email.mailId}&templateid=${email.templateId}&Mail0ToAddress_text=${emailAddress}&mergeid=${this.contact.id}`);
            } catch (error) {
                this.$error({ message: 'error while sending email' });
            }
        },

        webSite(address) {
            window.open(address.toString());
        },

        fieldEmptyCheck(value) {
            return !(value === '' || value === null);
        },

        interceptInput(field, ref) {
            if (field && field.fieldValue && field.fieldType === 'Website') {
                const hasPrefix = field.fieldValue.includes('http') || field.fieldValue.includes('https');
                const hasWww = field.fieldValue.includes('www.');
                const isDomainOnly = field.fieldValue.includes('.') && !hasPrefix && !hasWww;

                if ((hasWww && !hasPrefix) || isDomainOnly) {
                    const link = `http://${field.fieldValue}`;

                    this.updateInput(ref, link);
                }
            }

            if (field && field.fieldValue && field.fieldType === 'PhoneNumber') {
                const newValue = field.fieldValue.replace(/\D+\./g, '');

                this.updateInput(ref, newValue);
            }

            if (field && field.fieldValue && (field.fieldType === 'DecimalNumber' || field.fieldType === 'Currency' || field.fieldType === 'Percent')) {
                const inputArray = field.fieldValue.toString().replace(/[^\d.]/g, '').split('');

                if (inputArray.slice(inputArray.indexOf('.')).length > 3) {
                    this.updateInput(ref, Number(field.fieldValue).toFixed(2));
                }
            }

            if (field && field.fieldValue && field.fieldType === 'DateTime') {
                field.fieldValue = this.formatDateTimeField(field.fieldValue, 'YYYY-MM-DD HH:mm:ss');
            }
        },

        formatDateTimeField(date, format = 'll') {
            const formattedDate = moment(date).isValid() ? date : new Date(date);

            return moment(formattedDate).format(format);
        },

        formatDateTimeFieldToTZ(date, format = 'll') {
            return moment.tz(date, this.serverTimeZone).format(format);
        },

        updateInput(ref, value) {
            if (this.$refs[ref] && this.$refs[ref][0]) {
                this.$refs[ref][0].input_updateInput(value);
                this.$refs[ref][0].input_updateValueExternal(value);
            }
        },

        manageCustomField() {
            window.open('/Admin/custom/manageCustomFields.jsp?table=Contact');
        },

        formatOptions(customField) {
            const allOptions = [...customField.options];
            const options = [];

            if (customField.fieldType === 'Radio' && customField.options.length <= 0) {
                this.yesNo.forEach((option) => {
                    options.push({ value: option.value.toString(), label: option.label });
                });
            } else if (customField.fieldType === 'DayOfWeek'
                    || customField.fieldType === 'Month'
                    || customField.fieldType === 'User') {
                allOptions.forEach((option) => {
                    options.push({ value: Number(option.id), label: option.label });
                });
            } else {
                allOptions.forEach((option) => {
                    options.push({ value: option.id, label: option.label });
                });
            }

            return options;
        },

        formatDrillDownOptions(customField) {
            const allOptions = [...customField.options];
            const resultOptions = [];

            allOptions.forEach((category) => {
                const categoryOptions = [];

                category.options.forEach((drillDown) => {
                    categoryOptions.push({ value: Number(drillDown.id), label: drillDown.label });
                });

                resultOptions.push({ label: category.label, options: categoryOptions });
            });

            return resultOptions;
        },

        formatYesNoOptions(customField) {
            return customField.fieldValue === null ? this.yesNoSelected : this.yesNo;
        },
    },
};
</script>

<style lang="scss" scoped>
.custom-form {
    display: grid;
}

.custom-Tab {
    border-top: 1px solid $color-gray-lighter;
    margin-top: $spacing-200;
}

.custom-Tab-header {
    font-family: SulSans;
    font-size: 1.9rem;
    line-height: 1.33;
}

.custom-fields-group {
    display: flex;
    flex-direction: column;
    border-top: 1px solid $color-gray-lighter;
    margin-top: $spacing-200;
    padding-top: $spacing-300;
}

.no-custom-text {
    font-size: $font-size-lg;
    font-weight: 300;
}

.no-custom-fields {
    width: 100%;
    text-align: center;
    border-top: 1px solid $color-gray-lighter;
    margin-top:  $spacing-200;
}

.no-custom-label {
    color: $color-gray-darker;
    padding-top: $spacing-800;
    padding-bottom: $spacing-700;
}

.custom-fields-group-header {
    margin-bottom: $spacing-300;
}

.custom-fields-grid {
    --input-margin-bottom: 0;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-gap: $spacing-300;
}

.external-look {
    --icon-color:  #{$color-blue};;
    padding: $spacing-050;
}

.trailing-icon {
    display: contents;
}

.input-field {
    flex: 1;
}

@media screen and (max-width: 1200px) {
    .custom-fields-grid {
        grid-template-columns: 1fr 1fr;
    }
}

@media screen and ($small) {
    .custom-fields-grid {
        grid-template-columns: 1fr;
    }
}

.custom-fields-container:empty {
    display: none;
}

.search-input-field {
    --input-margin-bottom: 0;
}

.search {
    width: px-to-rem(320px);
    --icon-color: #{$color-gray-600};
    margin-top: $spacing-200;
    justify-content: flex-start;
}

.clear-icon {
    --icon-color: #{$color-ink};
    cursor: pointer;
}

.search-save-button {
    justify-content: space-between;
    display: flex;
    margin-top: $spacing-200;
}
</style>
